import React, { memo, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { PortWidget } from "@projectstorm/react-diagrams-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPuzzlePiece,
  faStopCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useParams } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import labels from "../helpers/labels";
import {
  editAction,
  selectEventHandlers,
  selectWorkflow,
} from "../../../../store/slices/projectSlice";
import Errors from "../../../Components/Errors";
import checkErrors from "../helpers/checkErrors";
import Context from "../../../../store/context";

function EventListenerSummary({ eventListener, node, engine, addChild }) {
  const { zoomToNode } = useContext(Context);
  const eventHandlers = useSelector(selectEventHandlers);
  const errors = useSelector(checkErrors, shallowEqual);
  const params = useParams();
  const eventHandler = useMemo(
    () =>
      eventHandlers.find((eh) => eh.UUID === eventListener.EventHandlerUUID),
    [eventHandlers, eventListener.EventHandlerUUID]
  );

  const rules = useMemo(
    () => [...(eventHandler?.Rules || [])].sort((a, b) => a.Order - b.Order),
    [eventHandler]
  );

  const counts = useMemo(
    () =>
      rules.reduce(
        (acc, rule) => ({
          conditions: acc.conditions + (rule.Conditions?.length || 0),
          actions: acc.actions + (rule.Actions?.length || 0),
        }),
        { conditions: 0, actions: 0 }
      ),
    [rules]
  );

  const hasErrors = errors.some(
    (err) => err.eventListener?.UUID === eventListener.UUID
  );

  return (
    eventHandler && (
      <div className="mt-2" key={eventHandler.UUID}>
        <Link
          className="btn btn-link px-0 fw-bold"
          to={`/library/logic/builder/${params.projectID}/workflow/${node.options.id}/event/${eventListener.EventType}/listener/${eventListener.UUID}`}
        >
          Listener: {eventHandler.Label} {eventHandler.IsModule && "(Module)"}
        </Link>
        <span className="d-inline-flex align-items-center gap-2 ms-2 flex-wrap">
          {eventHandler.IsModule && (
            <FontAwesomeIcon
              icon={faPuzzlePiece}
              title="Module"
              className="text-primary"
            />
          )}
          {eventListener.StopPropagation && (
            <FontAwesomeIcon
              icon={faStopCircle}
              title="Stops propagation"
              className="text-danger"
            />
          )}
          {hasErrors && (
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              title="Contains errors"
              className="text-danger"
            />
          )}
          <span
            className="badge rounded-pill bg-light text-secondary"
            title={`${counts.conditions} condition${counts.conditions === 1 ? "" : "s"}`}
          >
            {counts.conditions} condition{counts.conditions === 1 ? "" : "s"}
          </span>
          <span
            className="badge rounded-pill bg-light text-secondary"
            title={`${counts.actions} action${counts.actions === 1 ? "" : "s"}`}
          >
            {counts.actions} action{counts.actions === 1 ? "" : "s"}
          </span>
        </span>
        <div className="workflow-summary-rules ms-3 mt-1">
          {rules.map((rule) => {
            return (
              <div
                key={rule.UUID}
                className="workflow-summary-rule d-flex align-items-center flex-wrap gap-2 my-1 w-100"
              >
                <div className="d-flex align-items-center flex-wrap gap-2 w-100">
                  <span className="fw-bold">Rule:</span>
                  <span>{rule.Label}</span>
                </div>
                <div className="w-100 mt-1">
                  <Errors
                    filterFn={(e) =>
                      e.eventListener?.UUID === eventListener.UUID &&
                      e.rule?.UUID === rule.UUID
                    }
                  />
                </div>
              </div>
            );
          })}
          <div className="workflow-summary-port-rail">
            {rules.map((rule) => {
              const actions = [...(rule.Actions || [])].sort(
                (a, b) => a.Order - b.Order
              );

              return (
                <div key={rule.UUID} className="workflow-summary-port-row">
                  {actions.map((action) => {
                    if (action.Type !== "start:runtime" || action.IsSelf) {
                      return null;
                    }

                    let portName = `${eventListener.UUID}|${action.UUID}`;
                    let port = node.getPort(portName);
                    if (!port) {
                      portName = `${eventListener.UUID}-${action.UUID}`;
                      port = node.getPort(portName);
                    }

                    if (!port) {
                      return null;
                    }

                    const [link] = Object.values(port.getLinks());
                    const connectedNode = link?.getTargetPort?.()?.getNode?.();
                    const connectedWorkflowUUID =
                      connectedNode?.getID?.() || action.TypeUUID;

                    return (
                      <ConnectedPort
                        key={`${eventListener.UUID}-${action.UUID}`}
                        engine={engine}
                        port={port}
                        action={action}
                        connectedWorkflowUUID={connectedWorkflowUUID}
                        zoomToNode={zoomToNode}
                        addChild={addChild}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )
  );
}

function ConnectedPort({
  engine,
  port,
  action,
  connectedWorkflowUUID,
  zoomToNode,
  addChild,
}) {
  const dispatch = useDispatch();
  const connectedWorkflow = useSelector(
    selectWorkflow(connectedWorkflowUUID)
  );
  const [isLinkDeleted, setIsLinkDeleted] = useState(false);
  const [link] = Object.values(port.getLinks());
  const isConnected = !isLinkDeleted && Boolean(link || action.TypeUUID);
  const hasConnectedLink = Object.values(port.getLinks()).some((portLink) =>
    Boolean(portLink.getTargetPort?.())
  );
  const connectedNode = link?.getTargetPort?.()?.getNode?.();

  useEffect(() => {
    if (action.TypeUUID) {
      setIsLinkDeleted(false);
    }
  }, [action.TypeUUID]);

  const title = [
    labels[action.Type] || action.Type,
    connectedWorkflow?.Label,
  ]
    .filter(Boolean)
    .join(": ");

  const stopDiagramEvent = (event) => {
    event.stopPropagation();
  };

  const addNode = (event) => {
    stopDiagramEvent(event);
    const workflowUUID = uuidv4();
    setIsLinkDeleted(false);

    dispatch(
      editAction({
        actionUUID: action.UUID,
        field: "TypeUUID",
        value: workflowUUID,
      })
    );
    addChild(
      {
        workflow: {
          Label: `Workflow-${workflowUUID.slice(0, 4)}`,
          UUID: workflowUUID,
          IsEntrypoint: false,
          EventListeners: [],
        },
      },
      port.getNode().getID()
    );
  };

  const deleteLink = (event) => {
    stopDiagramEvent(event);
    setIsLinkDeleted(true);
    link?.remove();
    dispatch(
      editAction({
        actionUUID: action.UUID,
        field: "TypeUUID",
        value: "",
      })
    );
    engine.repaintCanvas();
  };

  return (
    <div className="workflow-summary-port-control">
      <PortWidget
        engine={engine}
        port={port}
        className="circle-porter circle-porter-out"
        action={action}
      >
        <div
          className="circle-port"
          title={title}
          onMouseDown={(event) => {
            if (hasConnectedLink) {
              event.stopPropagation();
            }
          }}
          onMouseMove={(event) => {
            if (event.buttons === 1 && hasConnectedLink) {
              event.stopPropagation();
              toast.error("You can drag only one link from a port.", {
                toastId: "connected-port-drag-error",
              });
            }
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            zoomToNode(connectedNode);
          }}
        />
      </PortWidget>
      <button
        type="button"
        className={`btn btn-outline-${isConnected ? "danger" : "primary"} btn-sm`}
        onMouseDown={stopDiagramEvent}
        onClick={isConnected ? deleteLink : addNode}
      >
        {isConnected ? "Delete link" : "Add Node"}
      </button>
    </div>
  );
}

export default memo(EventListenerSummary);
